/**
 * ObjectLabelToggleGroup
 *
 * Floating control on the canvas (top-left of CanvasArea) that lets the user
 * pick which fields show up under each object on the map: ID / IP / NAME.
 *
 * Stateless from the parent's perspective — it owns nothing beyond the click
 * handler. Persistence lives in objectLabelToggleStorage; the parent passes
 * the loaded values in via `value` and we write back through `onChange` plus
 * a save side-effect so refresh restores the user's last choice.
 */

import { useCallback } from 'react'
import {
  saveObjectLabelToggles,
  ObjectLabelToggles,
} from '../lib/objectLabelToggleStorage'
import styles from './ObjectLabelToggleGroup.module.css'

interface ObjectLabelToggleGroupProps {
  value: ObjectLabelToggles
  onChange: (next: ObjectLabelToggles) => void
}

type ToggleKey = keyof ObjectLabelToggles

interface ToggleConfig {
  key: ToggleKey
  label: string
  description: string
}

// Order here is the visual order in the toolbar AND the line order under each
// object (ID -> IP -> NAME). Keep them in sync if you ever reorder.
const TOGGLES: readonly ToggleConfig[] = [
  { key: 'id', label: 'ID', description: '객체 ID(앞 8자리) 표시' },
  { key: 'ip', label: 'IP', description: 'ipAddress 속성 표시' },
  { key: 'name', label: '이름', description: 'name / groupName 표시' },
] as const

export function ObjectLabelToggleGroup({
  value,
  onChange,
}: ObjectLabelToggleGroupProps) {
  const handleToggle = useCallback(
    (key: ToggleKey) => {
      const next: ObjectLabelToggles = { ...value, [key]: !value[key] }
      onChange(next)
      // Side-effect: persist. Failures are swallowed inside the adapter so a
      // private-mode browser never breaks the click.
      saveObjectLabelToggles(next)
    },
    [value, onChange],
  )

  return (
    <div
      className={styles.toggleGroup}
      role="group"
      aria-label="객체 라벨 표시 토글"
    >
      <span className={styles.heading}>표시</span>
      {TOGGLES.map(({ key, label, description }) => (
        <label
          key={key}
          className={styles.toggleLabel}
          title={description}
        >
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={value[key]}
            onChange={() => handleToggle(key)}
          />
          <span>{label}</span>
        </label>
      ))}
    </div>
  )
}
